// Settings → AI engine → Shared credits: lend your AI key to a mate, with an
// optional spending limit, end date and model list, and stop it any time.
// Credits a mate has shared with you are listed here too, and picked in the
// AI engine sheet.
//
// The key itself never leaves the server (see api/lib/creditShares.js), so
// every limit set here is enforced there, not trusted to the mate's browser.
// Owners see a mate's use by day, never by the minute; the mate sees their
// own requests in full.
//
// Credit invites live here too: a link or QR code that gives whoever claims
// it a small allowance (A$2 for a month, say) to try Khronos on. Each claim
// becomes an ordinary share, listed with the rest.

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Copy, Gift, Loader2, Plus, Share2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../ui/button";
import DetailSheet from "../session/generate/DetailSheet";
import { ChipGrid, DeleteConfirm, OptionList } from "../profile/ProfileParts";
import HelpLink from "../help/HelpLink";
import api from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { useSocial } from "../../contexts/SocialContext";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { LLM_PROVIDERS, formatModelRate } from "../../config/llmProviders";
import { getProviderApiKey } from "../../utils/llmQuickSwitch";
import { buildInviteUrl, copyTextToClipboard, shareOrCopyLink } from "../../utils/shareLinks";
import { cn } from "../../lib/utils";
import { describeRate, displayCurrency, formatAmount } from "../../utils/money";
import {
  EXPIRY_CHOICES,
  INVITE_DAYS_CHOICES,
  INVITE_PEOPLE_CHOICES,
  canAskForMore,
  describeCreditInvite,
  describeEnded,
  describeInviteTotal,
  describeExpiry,
  describeLastUsed,
  expiryFromChoice,
  firstName,
  formatTime,
  groupByDay,
  inviteAmountChoices,
  limitChoicesFor,
  limitFromChoice,
  shareMeter,
  shareMoney,
  shareTag,
  shareTitle,
  shortModel,
  todayKey,
} from "../../utils/creditShares";
import {
  ActivityRow,
  Avatar,
  DayHeading,
  ShareCard,
  ShareRow,
  SheetGroup,
  ValueRow,
} from "./creditParts";

export const useCreditShares = (enabled = true) => {
  const [state, setState] = useState({ loading: true, given: [], received: [], error: null });

  const load = useCallback(async () => {
    try {
      const res = await api.getCreditShares();
      setState({ loading: false, given: res.given || [], received: res.received || [], error: null });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e.message }));
    }
  }, []);

  useEffect(() => {
    if (enabled) load();
    else setState({ loading: false, given: [], received: [], error: null });
  }, [enabled, load]);

  return { ...state, reload: load };
};

/** Your open credit invite links, newest first. */
const useCreditInvites = (enabled) => {
  const [invites, setInvites] = useState([]);
  const load = useCallback(async () => {
    try {
      const rows = await api.getInvites();
      setInvites((rows || []).filter((i) => i.kind === "credit"));
    } catch {
      // The list is a convenience; shares still load without it.
    }
  }, []);
  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);
  return { invites, reload: load };
};

/** One line for the Settings row. */
export const describeCreditShares = ({ given, received }) => {
  const sharing = given.filter((s) => s.status === "active").length;
  const borrowing = received.filter((s) => s.status === "active").length;
  if (sharing && borrowing) return `Sharing with ${sharing} · ${borrowing} for you`;
  if (sharing) return `Sharing with ${sharing}`;
  if (borrowing) return `${borrowing} shared with you`;
  return "Off";
};

const DAYS_SHOWN = 4;
const CALLS_SHOWN = 6;

/** A share's activity: days for the owner, requests for the mate. */
const useShareUsage = (share) => {
  const [state, setState] = useState({ loading: true, days: null, calls: null, error: null });
  useEffect(() => {
    if (!share) return undefined;
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    api
      .getCreditShareUsage(share.id)
      .then((res) => live && setState({ loading: false, days: res.days || null, calls: res.calls || null, error: null }))
      .catch((e) => live && setState({ loading: false, days: null, calls: null, error: e.message }));
    return () => {
      live = false;
    };
  }, [share?.id, share?.uses]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
};

const ShowMore = ({ shown, total, all, onToggle, noun }) =>
  total > shown || all ? (
    <button
      type="button"
      onClick={onToggle}
      className="w-full px-3 py-2.5 text-left text-sm font-semibold text-primary transition-colors hover:bg-muted/30"
    >
      {all ? "Show fewer" : `Show all ${total} ${noun}`}
    </button>
  ) : null;

const LoadingLine = () => (
  <p className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
    <Loader2 className="h-4 w-4 animate-spin" /> Loading…
  </p>
);

/** The owner's view of a mate's use: one line per day. */
const OwnerActivity = ({ share }) => {
  const usage = useShareUsage(share);
  const [all, setAll] = useState(false);
  const days = usage.days || [];
  const shown = all ? days : days.slice(0, DAYS_SHOWN);
  return (
    <SheetGroup title="Activity" aside={describeLastUsed(share)}>
      {usage.loading && !usage.days ? (
        <LoadingLine />
      ) : usage.error ? (
        <p className="px-3 py-3 text-sm text-destructive">{usage.error}</p>
      ) : days.length === 0 ? (
        <p className="px-3 py-3 text-sm text-muted-foreground">Nothing yet. Each day {firstName(share.recipient.name)} uses these credits shows here.</p>
      ) : (
        <>
          {groupByDay(shown, (d) => d.date).map((group) =>
            group.items.map((day) => (
              <ActivityRow
                key={day.date}
                title={group.label}
                detail={`${day.requests} ${day.requests === 1 ? "request" : "requests"} · ${day.models.map(shortModel).join(", ")}`}
                amount={day.unpriced && !day.cost ? "–" : formatAmount(day.cost, share.currency, { precise: true })}
                note={day.estimated ? "est." : undefined}
              />
            )),
          )}
          <ShowMore shown={DAYS_SHOWN} total={days.length} all={all} onToggle={() => setAll((a) => !a)} noun="days" />
        </>
      )}
    </SheetGroup>
  );
};

/** The mate's own requests on a share, with times, grouped by day. */
const MyActivity = ({ share }) => {
  const usage = useShareUsage(share);
  const [all, setAll] = useState(false);
  const calls = usage.calls || [];
  const shown = all ? calls : calls.slice(0, CALLS_SHOWN);
  return (
    <SheetGroup title="Your activity" aside={describeLastUsed(share)}>
      {usage.loading && !usage.calls ? (
        <LoadingLine />
      ) : usage.error ? (
        <p className="px-3 py-3 text-sm text-destructive">{usage.error}</p>
      ) : calls.length === 0 ? (
        <p className="px-3 py-3 text-sm text-muted-foreground">Nothing yet. Each session you write on these credits shows here.</p>
      ) : (
        <>
          {groupByDay(shown, (c) => todayKey(new Date(c.at))).map((group) => (
            <React.Fragment key={group.key}>
              <DayHeading>{group.label}</DayHeading>
              {group.items.map((call, i) => (
                <ActivityRow
                  key={`${call.at}-${i}`}
                  title={shortModel(call.model)}
                  detail={formatTime(call.at)}
                  amount={call.cost == null ? "–" : formatAmount(call.cost, share.currency, { precise: true })}
                  note={call.exact ? undefined : "est."}
                />
              ))}
            </React.Fragment>
          ))}
          <ShowMore shown={CALLS_SHOWN} total={calls.length} all={all} onToggle={() => setAll((a) => !a)} noun="requests" />
        </>
      )}
    </SheetGroup>
  );
};

/** Several-of-many model picker, styled like OptionList. */
const ModelChecklist = ({ provider, value, onChange, money }) => {
  const models = LLM_PROVIDERS[provider]?.models || [];
  const toggle = (model) => onChange(value.includes(model) ? value.filter((m) => m !== model) : [...value, model]);
  return (
    <div className="grid gap-1.5">
      {models.map((model) => {
        const on = value.includes(model);
        return (
          <button
            key={model}
            type="button"
            aria-pressed={on}
            onClick={() => toggle(model)}
            className={cn(
              "grid grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-x-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors",
              on ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/40",
            )}
          >
            <span
              className={cn(
                "flex h-5 w-5 items-center justify-center rounded-md border",
                on ? "border-primary bg-primary" : "border-muted",
              )}
            >
              {on && <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />}
            </span>
            <span className="min-w-0">
              <span className={cn("block truncate text-sm font-semibold", on && "text-primary")}>{shortModel(model)}</span>
              <span className="block truncate text-xs text-muted-foreground">{formatModelRate(provider, model, money)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

const dayLabel = (iso) => new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });

/** Spending limit options for a share or draft, each saying what it leaves. */
const limitOptions = ({ currency, current, spent, whoPays }) =>
  limitChoicesFor(currency).map((choice) => {
    const amount = limitFromChoice(choice.value);
    let description;
    if (amount == null) description = whoPays;
    else if (spent > 0) description = amount > spent ? `${formatAmount(amount - spent, currency)} left after what's been used` : "Below what's been used, so sharing pauses";
    return {
      value: choice.value,
      label: choice.label,
      description,
      aside: current === choice.value ? "Current" : undefined,
    };
  });

/** End date options; an end date that isn't one of the choices is offered as Keep. */
const endsOptions = ({ expiresAt, currentChoice }) => {
  const now = new Date();
  const options = [];
  if (expiresAt && !currentChoice) {
    const days = Math.max(0, Math.round((new Date(expiresAt) - now) / 86_400_000));
    options.push({ value: "keep", label: `Keep ${dayLabel(expiresAt)}`, description: `${days} ${days === 1 ? "day" : "days"} from today`, aside: "Current" });
  }
  for (const choice of EXPIRY_CHOICES) {
    const at = expiryFromChoice(choice.value, now);
    options.push({
      value: choice.value,
      label: choice.label,
      description: at ? `Until ${dayLabel(at)}` : "Until you stop it",
      aside: currentChoice === choice.value ? "Current" : undefined,
    });
  }
  return options;
};

// A share's current limit as a choice value, when it is one of the steps.
const limitChoiceOf = (share) => {
  if (share.limitAmount == null) return "none";
  const step = limitChoicesFor(share.currency).find((c) => Number(c.value) === share.limitAmount);
  return step ? step.value : `custom:${share.limitAmount}`;
};

const NEW_DRAFT = { recipientId: null, provider: null, models: null, limit: null, expiry: "month" };
const NEW_INVITE = { provider: null, models: null, amount: "2", days: "30", people: "5" };

const CreditSharesSheet = ({ open, credits, onClose, onUseShare }) => {
  const { showToast } = useToast();
  const { friends = [] } = useSocial() || {};
  const { llmSettings, money } = useContext(WorkoutContext);
  const [view, setView] = useState({ name: "main" });
  const [draft, setDraft] = useState(NEW_DRAFT);
  // The models being picked in the Models view, saved on leaving it.
  const [models, setModels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState(null);
  const [endedOpen, setEndedOpen] = useState(false);
  const [inviteDraft, setInviteDraft] = useState(NEW_INVITE);
  const [copied, setCopied] = useState(false);
  const [asked, setAsked] = useState({});
  const creditInvites = useCreditInvites(open);

  useEffect(() => {
    if (!open) return;
    setView({ name: "main" });
    setNotice(null);
    credits.reload();
    creditInvites.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // New shares are capped in the viewer's currency (US$ if there's no rate).
  const newCurrency = displayCurrency(money);
  const ownProviders = useMemo(
    () =>
      Object.entries(LLM_PROVIDERS)
        .filter(([key, config]) => config.apiKeyRequired && getProviderApiKey(llmSettings, key))
        .map(([key, config]) => ({ value: key, label: config.label })),
    [llmSettings],
  );

  const activeGiven = credits.given.filter((s) => s.status === "active");
  const openInvites = creditInvites.invites.filter((i) => i.state === "open");
  const showing = creditInvites.invites.find((i) => i.code === view.code) || null;
  const activeReceived = credits.received.filter((s) => s.status === "active");
  const ended = [...credits.given, ...credits.received].filter((s) => s.status !== "active").slice(0, 8);
  const editing = credits.given.find((s) => s.id === view.id) || null;
  const viewing = credits.received.find((s) => s.id === view.id) || null;
  const from = view.from;
  const forNew = from === "new";
  const forInvite = from === "invite";

  const go = (name, extra = {}) => {
    setConfirming(false);
    setView({ name, ...extra });
  };

  const startNew = () => {
    setDraft({
      ...NEW_DRAFT,
      limit: limitChoicesFor(newCurrency)[0].value,
      recipientId: friends.length === 1 ? friends[0].id : null,
      provider: ownProviders.length === 1 ? ownProviders[0].value : null,
    });
    setNotice(null);
    go("new");
  };

  const save = async (share, changes, message) => {
    try {
      await api.updateCreditShare(share.id, changes);
      await credits.reload();
      if (message) showToast({ title: message });
    } catch (e) {
      showToast({ title: "Couldn't save that change", description: e.message });
    }
  };

  const pickLimit = (value) => {
    if (forNew) setDraft((d) => ({ ...d, limit: value }));
    else if (editing && !value.startsWith("custom:")) {
      save(editing, { limitAmount: limitFromChoice(value), limitCurrency: editing.currency }, `Limit for ${firstName(editing.recipient.name)} updated`);
    }
    go(forNew ? "new" : "detail", forNew ? {} : { id: editing?.id });
  };

  const pickEnds = (value) => {
    if (forNew) setDraft((d) => ({ ...d, expiry: value }));
    else if (editing && value !== "keep") {
      save(editing, { expiresAt: expiryFromChoice(value) }, `End date for ${firstName(editing.recipient.name)} updated`);
    }
    go(forNew ? "new" : "detail", forNew ? {} : { id: editing?.id });
  };

  const openModels = (source) => {
    setModels(source === "new" ? draft.models : source === "invite" ? inviteDraft.models : editing?.allowedModels || null);
    go("models", { from: source, id: editing?.id });
  };

  // Leaving Models: nothing ticked means any model, as on the server.
  const leaveModels = async () => {
    const chosen = Array.isArray(models) && models.length ? models : null;
    if (forInvite) {
      setInviteDraft((d) => ({ ...d, models: chosen }));
      return go("invite");
    }
    if (forNew) setDraft((d) => ({ ...d, models: chosen }));
    else if (editing && JSON.stringify(chosen) !== JSON.stringify(editing.allowedModels || null)) {
      await save(editing, { allowedModels: chosen }, `Models for ${firstName(editing.recipient.name)} updated`);
    }
    go(forNew ? "new" : "detail", forNew ? {} : { id: editing?.id });
  };

  const create = async () => {
    setBusy(true);
    try {
      await api.createCreditShare({
        recipientId: draft.recipientId,
        provider: draft.provider,
        allowedModels: draft.models,
        limitAmount: limitFromChoice(draft.limit),
        limitCurrency: newCurrency,
        expiresAt: expiryFromChoice(draft.expiry),
      });
      const mate = friends.find((f) => f.id === draft.recipientId);
      await credits.reload();
      setNotice(`Sharing with ${firstName(mate?.name)}. They've been told.`);
      go("main");
    } catch (e) {
      showToast({ title: "Couldn't share your credits", description: e.message });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.revokeCreditShare(editing.id);
      await credits.reload();
      setNotice(`Stopped sharing with ${firstName(editing.recipient.name)}.`);
      go("main");
    } catch (e) {
      showToast({ title: "Couldn't stop sharing", description: e.message });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const startInvite = () => {
    setInviteDraft({ ...NEW_INVITE, provider: ownProviders.length === 1 ? ownProviders[0].value : null });
    setNotice(null);
    go("invite");
  };

  const createInviteLink = async () => {
    setBusy(true);
    try {
      const invite = await api.createInvite({
        kind: "credit",
        provider: inviteDraft.provider,
        models: inviteDraft.models,
        amount: Number(inviteDraft.amount),
        currency: newCurrency,
        days: Number(inviteDraft.days),
        maxUses: Number(inviteDraft.people),
      });
      await creditInvites.reload();
      go("link", { code: invite.code });
    } catch (e) {
      showToast({ title: "Couldn't make the invite", description: e.message });
    } finally {
      setBusy(false);
    }
  };

  const stopLink = async () => {
    setBusy(true);
    try {
      await api.revokeInvite(showing.code);
      await creditInvites.reload();
      setNotice("Invite link stopped. Credits already claimed keep going.");
      go("main");
    } catch (e) {
      showToast({ title: "Couldn't stop the link", description: e.message });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const shareLink = async (invite) => {
    const url = buildInviteUrl(invite.code);
    const outcome = await shareOrCopyLink({
      url,
      title: "Try Khronos on me",
      text: `Here's ${formatAmount(Number(invite.credit_amount), invite.credit_currency).replace(".00", "")} of AI credits to try Khronos.`,
    });
    if (outcome === "copied") {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyLink = async (invite) => {
    if (await copyTextToClipboard(buildInviteUrl(invite.code))) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const askForMore = async (share) => {
    try {
      await api.askForMoreCredits(share.id);
      setAsked((a) => ({ ...a, [share.id]: true }));
      showToast({ title: `Asked ${firstName(share.owner.name)} for more` });
    } catch (e) {
      showToast({ title: "Couldn't ask", description: e.message });
    }
  };

  const back = () => {
    if (view.name === "models") return leaveModels();
    if (view.name === "mate" || view.name === "provider") return go("new");
    if (["inviteAmount", "inviteDays", "invitePeople", "inviteProvider"].includes(view.name)) return go("invite");
    if (["limit", "ends"].includes(view.name)) return go(forNew ? "new" : "detail", forNew ? {} : { id: view.id });
    return go("main");
  };

  const close = async () => {
    if (view.name === "models" && !forNew && !forInvite) await leaveModels();
    onClose();
  };

  // ── Views ──────────────────────────────────────────────

  const givenRow = (share) => {
    const left = share.limitAmount == null ? null : share.remaining;
    return (
      <ShareRow
        key={share.id}
        name={share.recipient.name}
        detail={`${share.providerLabel} · ${describeExpiry(share)}`}
        tag={shareTag(share)}
        figure={shareMoney(share, left == null ? share.spent : left)}
        figureLabel={left == null ? "used, no limit" : "left"}
        meter={shareMeter(share)}
        onClick={() => go("detail", { id: share.id })}
      />
    );
  };

  const receivedRow = (share) => {
    const inUse = llmSettings.creditShare?.id === share.id;
    const left = share.limitAmount == null ? null : share.remaining;
    return (
      <ShareRow
        key={share.id}
        name={shareTitle(share)}
        avatarName={share.owner.name}
        detail={describeExpiry(share)}
        tag={inUse ? { label: "In use", tone: "primary" } : shareTag(share)}
        figure={shareMoney(share, left == null ? share.spent : left)}
        figureLabel={left == null ? "used, no limit" : "left"}
        meter={shareMeter(share)}
        onClick={() => go("received", { id: share.id })}
      />
    );
  };

  const card = (share, { name, avatarName, detail, tag }) => {
    const left = share.limitAmount == null ? null : share.remaining;
    return (
      <ShareCard
        name={name}
        avatarName={avatarName}
        detail={detail}
        tag={tag}
        hero={shareMoney(share, left == null ? share.spent : left)}
        heroLabel={left == null ? "used, no limit" : `left of ${shareMoney(share, share.limitAmount)}`}
        meter={shareMeter(share)}
        stats={[
          { label: "Used", value: shareMoney(share, share.spent) },
          { label: "Requests", value: String(share.uses) },
          { label: "Ends", value: share.expiresAt ? dayLabel(share.expiresAt) : "Never" },
        ]}
      />
    );
  };

  const mainView = (
    <>
      {notice && (
        <p role="status" className="flex items-center gap-2.5 rounded-xl border border-[#3987e5]/40 bg-[#3987e5]/10 px-3 py-2.5 text-[13px]">
          <Check className="h-4 w-4 shrink-0 text-[#3987e5]" strokeWidth={2.5} />
          {notice}
        </p>
      )}
      <p className="text-[13px] leading-snug text-muted-foreground">
        Lend your AI key to a mate. Your key stays on the Khronos server, and you can stop any time.{" "}
        <HelpLink topic="ai-options#share-your-ai-credits" label="How it works" />
      </p>

      <SheetGroup title="You're sharing">
        {activeGiven.map(givenRow)}
        <button
          type="button"
          onClick={startNew}
          className="grid w-full grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-2.5 px-3 py-3 text-left text-sm font-semibold text-primary transition-colors hover:bg-muted/30"
        >
          <Plus className="h-[18px] w-[18px]" />
          Share with a mate
        </button>
        <button
          type="button"
          onClick={startInvite}
          className="grid w-full grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-2.5 px-3 py-3 text-left text-sm font-semibold text-primary transition-colors hover:bg-muted/30"
        >
          <Gift className="h-[18px] w-[18px]" />
          Invite someone with credits
        </button>
      </SheetGroup>

      {openInvites.length > 0 && (
        <SheetGroup title="Invite links">
          {openInvites.map((invite) => (
            <ValueRow
              key={invite.code}
              label={describeCreditInvite(invite)}
              value={invite.expires_at ? `Open until ${dayLabel(invite.expires_at)}` : "Open"}
              onClick={() => go("link", { code: invite.code })}
            />
          ))}
        </SheetGroup>
      )}

      {activeReceived.length > 0 && <SheetGroup title="Shared with you">{activeReceived.map(receivedRow)}</SheetGroup>}

      {ended.length > 0 && (
        <SheetGroup>
          <button
            type="button"
            aria-expanded={endedOpen}
            onClick={() => setEndedOpen((o) => !o)}
            className="grid w-full grid-cols-[minmax(0,1fr)_auto_1rem] items-center gap-2.5 px-3 py-3 text-left transition-colors hover:bg-muted/30"
          >
            <span className="text-sm font-medium">Ended</span>
            <span className="text-[13px] text-muted-foreground">{ended.length}</span>
            <ChevronDown className={cn("h-4 w-4 text-muted-foreground/70 transition-transform", endedOpen && "rotate-180")} />
          </button>
          {endedOpen &&
            ended.map((share) => {
              const mine = credits.given.includes(share);
              const who = mine ? share.recipient.name : share.owner.name;
              // Your own ended (not stopped) shares open, so you can top one up.
              const topUp = mine && share.status !== "revoked";
              const Row = topUp ? "button" : "div";
              return (
                <Row
                  key={share.id}
                  {...(topUp ? { type: "button", onClick: () => go("detail", { id: share.id }) } : {})}
                  className={cn(
                    "grid w-full grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-2.5 px-3 py-2.5 text-left text-muted-foreground",
                    topUp && "transition-colors hover:bg-muted/30",
                  )}
                >
                  <Avatar name={who} size={32} muted />
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] text-foreground">
                      {mine ? `${who} · ${share.providerLabel}` : shareTitle(share)}
                    </span>
                    <span className="block text-xs">{describeEnded(share)}</span>
                  </span>
                  <span className="text-xs tabular-nums">{shareMoney(share, share.spent)} used</span>
                </Row>
              );
            })}
        </SheetGroup>
      )}

      {credits.error && <p className="text-sm text-destructive">{credits.error}</p>}
      {describeRate(money) && (
        <p className="text-xs leading-snug text-muted-foreground">
          Amounts in A$ use {describeRate(money)}. Providers bill in US$.
        </p>
      )}
    </>
  );

  const detailView = editing && (
    <>
      {card(editing, {
        name: editing.recipient.name,
        detail: `${editing.providerLabel} · shared ${dayLabel(editing.createdAt)}`,
        tag: shareTag(editing),
      })}
      {editing.status !== "active" && (
        <p className="text-[13px] leading-snug text-muted-foreground">
          This share has {editing.status === "spent" ? "used its limit" : "ended"}. Raise the limit or pick a new end date
          to top it up, and {firstName(editing.recipient.name)} can carry on.
        </p>
      )}
      <SheetGroup title="Limits">
        <ValueRow
          label="Spending limit"
          value={editing.limitAmount == null ? "No limit" : shareMoney(editing, editing.limitAmount)}
          onClick={() => go("limit", { from: "detail", id: editing.id })}
        />
        <ValueRow
          label="Ends"
          value={editing.expiresAt ? dayLabel(editing.expiresAt) : "No end date"}
          onClick={() => go("ends", { from: "detail", id: editing.id })}
        />
        <ValueRow
          label="Models"
          value={editing.allowedModels ? `${editing.allowedModels.length} models` : "Any model"}
          onClick={() => openModels("detail")}
        />
      </SheetGroup>
      <OwnerActivity share={editing} />
      <p className="text-xs leading-snug text-muted-foreground">
        You see the days {firstName(editing.recipient.name)} used your credits and what they cost, not when or what they
        wrote. You&rsquo;re both told at 80% and when they run out.
      </p>
      <DeleteConfirm
        name={`with ${firstName(editing.recipient.name)}`}
        consequence="Their next request will be refused. Anything already running finishes."
        confirming={confirming}
        onAsk={() => setConfirming(true)}
        onCancel={() => setConfirming(false)}
        onConfirm={revoke}
        busy={busy}
        action="Stop sharing"
      />
    </>
  );

  const limitTarget = forNew
    ? { currency: newCurrency, current: draft.limit, spent: 0 }
    : editing && { currency: editing.currency, current: limitChoiceOf(editing), spent: editing.spent };
  const limitView = limitTarget && (
    <>
      <p className="text-[13px] text-muted-foreground">
        {forNew
          ? "What your mate can spend on your key before it pauses."
          : `${shareMoney(editing, editing.spent)} used so far.`}
      </p>
      <OptionList
        options={[
          ...(limitTarget.current?.startsWith("custom:")
            ? [{ value: limitTarget.current, label: `Keep ${formatAmount(editing.limitAmount, editing.currency)}`, aside: "Current" }]
            : []),
          ...limitOptions({
            ...limitTarget,
            whoPays: forNew ? "You pay for whatever they use" : `You pay for whatever ${firstName(editing.recipient.name)} uses`,
          }),
        ]}
        value={limitTarget.current}
        onChange={pickLimit}
      />
      {limitTarget.currency === "AUD" && (
        <p className="text-xs leading-snug text-muted-foreground">
          Providers bill in US$. Each session is converted to A$ at that day&rsquo;s rate.
        </p>
      )}
    </>
  );

  const endsTarget = forNew
    ? { expiresAt: null, currentChoice: draft.expiry }
    : editing && { expiresAt: editing.expiresAt, currentChoice: editing.expiresAt ? null : "none" };
  const endsView = endsTarget && (
    <OptionList
      options={endsOptions(endsTarget)}
      value={endsTarget.currentChoice || "keep"}
      onChange={pickEnds}
    />
  );

  const modelsProvider = forNew ? draft.provider : forInvite ? inviteDraft.provider : editing?.provider;
  const modelsView = (
    <>
      <ChipGrid
        options={[
          { value: "any", label: "Any model" },
          { value: "some", label: "Only some" },
        ]}
        value={Array.isArray(models) ? "some" : "any"}
        onChange={(mode) => setModels(mode === "some" ? models || [] : null)}
      />
      {Array.isArray(models) ? (
        <>
          <ModelChecklist provider={modelsProvider} value={models} onChange={setModels} money={money} />
          {models.length === 0 && <p className="text-xs text-muted-foreground">Tick at least one, or it stays any model.</p>}
        </>
      ) : (
        <p className="text-[13px] leading-snug text-muted-foreground">
          Any {LLM_PROVIDERS[modelsProvider]?.label || ""} model in Khronos, cheapest to most capable. The spending limit
          still applies.
        </p>
      )}
    </>
  );

  const mate = friends.find((f) => f.id === draft.recipientId);
  const draftLimit = limitFromChoice(draft.limit);
  const newView = (
    <>
      <p className="text-[13px] leading-snug text-muted-foreground">
        Your mate builds sessions on your key. They never see it, and you can change or stop this any time.
      </p>
      <SheetGroup>
        <ValueRow
          label="Mate"
          value={mate ? mate.name : friends.length ? "Choose" : "Add mates first"}
          valueClassName={mate ? undefined : "font-semibold text-primary"}
          onClick={friends.length ? () => go("mate", { from: "new" }) : undefined}
        />
        <ValueRow
          label="Provider"
          value={
            draft.provider
              ? LLM_PROVIDERS[draft.provider]?.label
              : ownProviders.length
                ? "Choose"
                : "Add a key in AI engine"
          }
          valueClassName={draft.provider ? (ownProviders.length > 1 ? undefined : "text-muted-foreground") : "font-semibold text-primary"}
          onClick={ownProviders.length > 1 ? () => go("provider", { from: "new" }) : undefined}
        />
        <ValueRow
          label="Spending limit"
          value={draftLimit == null ? "No limit" : formatAmount(draftLimit, newCurrency)}
          onClick={() => go("limit", { from: "new" })}
        />
        <ValueRow
          label="Ends"
          value={EXPIRY_CHOICES.find((c) => c.value === draft.expiry)?.label || "No end date"}
          onClick={() => go("ends", { from: "new" })}
        />
        <ValueRow
          label="Models"
          value={draft.models ? `${draft.models.length} models` : "Any model"}
          onClick={draft.provider ? () => openModels("new") : undefined}
        />
      </SheetGroup>
      <p className="text-xs leading-snug text-muted-foreground">
        Only providers you&rsquo;ve added a key for. Your mate sees what&rsquo;s left; you see the days they used it and
        what it cost.
      </p>
    </>
  );

  const sharingWith = new Set(activeGiven.filter((s) => s.provider === draft.provider).map((s) => s.recipient.id));
  const mateView = (
    <div className="grid gap-1.5">
      {friends.map((friend) => {
        const selected = draft.recipientId === friend.id;
        return (
          <button
            key={friend.id}
            type="button"
            aria-pressed={selected}
            onClick={() => {
              setDraft((d) => ({ ...d, recipientId: friend.id }));
              go("new");
            }}
            className={cn(
              "grid grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
              selected ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/40",
            )}
          >
            <Avatar name={friend.name} />
            <span className={cn("truncate text-sm font-semibold", selected && "text-primary")}>{friend.name}</span>
            {sharingWith.has(friend.id) && <span className="text-xs text-muted-foreground">Replaces current share</span>}
          </button>
        );
      })}
    </div>
  );

  const providerView = (
    <OptionList
      options={ownProviders}
      value={draft.provider}
      onChange={(provider) => {
        setDraft((d) => ({ ...d, provider, models: null }));
        go("new");
      }}
    />
  );

  const inUse = viewing && llmSettings.creditShare?.id === viewing.id;
  const receivedView = viewing && (
    <>
      {card(viewing, {
        name: shareTitle(viewing),
        avatarName: viewing.owner.name,
        detail: `Shared with you ${dayLabel(viewing.createdAt)}`,
        tag: inUse ? { label: "In use", tone: "primary" } : shareTag(viewing),
      })}
      {canAskForMore(viewing) && (viewing.status !== "active" || shareTag(viewing)) && (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={Boolean(asked[viewing.id])}
          onClick={() => askForMore(viewing)}
        >
          {asked[viewing.id] ? `Asked ${firstName(viewing.owner.name)}` : `Ask ${firstName(viewing.owner.name)} for more`}
        </Button>
      )}
      <MyActivity share={viewing} />
      <p className="text-xs leading-snug text-muted-foreground">
        {firstName(viewing.owner.name)} pays for what you write on these credits. They see the days you used them and
        what it cost, not when or what you wrote. To go back to your own setup, pick another provider in AI engine.
      </p>
    </>
  );

  const inviteTotal = describeInviteTotal({ amount: inviteDraft.amount, people: inviteDraft.people, currency: newCurrency });
  const inviteView = (
    <>
      <p className="text-[13px] leading-snug text-muted-foreground">
        Make a link or QR code for friends and family to try Khronos on your key. Each person who claims it gets their
        own allowance, and becomes your mate.
      </p>
      <SheetGroup>
        <ValueRow
          label="Provider"
          value={
            inviteDraft.provider
              ? LLM_PROVIDERS[inviteDraft.provider]?.label
              : ownProviders.length
                ? "Choose"
                : "Add a key in AI engine"
          }
          valueClassName={inviteDraft.provider ? (ownProviders.length > 1 ? undefined : "text-muted-foreground") : "font-semibold text-primary"}
          onClick={ownProviders.length > 1 ? () => go("inviteProvider") : undefined}
        />
        <ValueRow
          label="Each person gets"
          value={formatAmount(Number(inviteDraft.amount), newCurrency).replace(".00", "")}
          onClick={() => go("inviteAmount")}
        />
        <ValueRow
          label="Lasts"
          value={INVITE_DAYS_CHOICES.find((c) => c.value === inviteDraft.days)?.label}
          onClick={() => go("inviteDays")}
        />
        <ValueRow
          label="Up to"
          value={INVITE_PEOPLE_CHOICES.find((c) => c.value === inviteDraft.people)?.label}
          onClick={() => go("invitePeople")}
        />
        <ValueRow
          label="Models"
          value={inviteDraft.models ? `${inviteDraft.models.length} models` : "Any model"}
          onClick={inviteDraft.provider ? () => openModels("invite") : undefined}
        />
      </SheetGroup>
      <ul className="grid gap-1 text-xs leading-snug text-muted-foreground">
        <li>{inviteTotal}. Whatever isn&rsquo;t used is never charged.</li>
        <li>Each allowance runs from the day it&rsquo;s claimed, then ends on its own.</li>
        <li>One claim per person, and not for anyone you&rsquo;ve already shared with.</li>
      </ul>
    </>
  );

  const inviteOptions = {
    inviteProvider: {
      title: "Provider",
      options: ownProviders,
      key: "provider",
    },
    inviteAmount: {
      title: "Each person gets",
      options: inviteAmountChoices(newCurrency),
      key: "amount",
    },
    inviteDays: {
      title: "Lasts",
      options: INVITE_DAYS_CHOICES.map((c) => ({ ...c, description: "From the day they claim it" })),
      key: "days",
    },
    invitePeople: {
      title: "Up to",
      options: INVITE_PEOPLE_CHOICES.map((c) => ({
        ...c,
        description: describeInviteTotal({ amount: inviteDraft.amount, people: c.value, currency: newCurrency }),
      })),
      key: "people",
    },
  };
  const pickerView = (name) => {
    const picker = inviteOptions[name];
    return (
      <OptionList
        options={picker.options}
        value={inviteDraft[picker.key]}
        onChange={(value) => {
          // Models belong to a provider, so a new provider starts at any model.
          setInviteDraft((d) => ({ ...d, [picker.key]: value, ...(picker.key === "provider" && value !== d.provider ? { models: null } : {}) }));
          go("invite");
        }}
      />
    );
  };

  const linkView = showing && (
    <>
      <div className="flex justify-center">
        <div className="rounded-xl bg-white p-3">
          <QRCodeSVG value={buildInviteUrl(showing.code)} size={200} level="M" bgColor="#ffffff" fgColor="#000000" />
        </div>
      </div>
      <p className="text-center text-[13px] leading-snug text-muted-foreground">
        Scan with a phone camera, or share the link. {describeCreditInvite(showing)}
        {showing.expires_at ? `, open until ${dayLabel(showing.expires_at)}` : ""}.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" onClick={() => copyLink(showing)}>
          {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
          {copied ? "Copied" : "Copy link"}
        </Button>
        <Button type="button" variant="outline" onClick={() => shareLink(showing)}>
          <Share2 className="mr-2 h-4 w-4" />
          Share
        </Button>
      </div>
      <p className="text-xs leading-snug text-muted-foreground">
        People who claim it show under You&rsquo;re sharing, like any mate. You&rsquo;re told when someone claims it and
        when they write their first session.
      </p>
      <DeleteConfirm
        name="this link"
        consequence="Nobody new can claim it. Credits already claimed keep going until they end."
        confirming={confirming}
        onAsk={() => setConfirming(true)}
        onCancel={() => setConfirming(false)}
        onConfirm={stopLink}
        busy={busy}
        action="Stop"
      />
    </>
  );

  const views = {
    main: { title: "Shared credits", body: mainView },
    invite: { title: "Invite with credits", body: inviteView },
    inviteProvider: { title: inviteOptions.inviteProvider.title, body: pickerView("inviteProvider") },
    inviteAmount: { title: inviteOptions.inviteAmount.title, body: pickerView("inviteAmount") },
    inviteDays: { title: inviteOptions.inviteDays.title, body: pickerView("inviteDays") },
    invitePeople: { title: inviteOptions.invitePeople.title, body: pickerView("invitePeople") },
    link: { title: "Invite link", body: linkView },
    detail: { title: "Your share", body: detailView },
    limit: { title: "Spending limit", body: limitView },
    ends: { title: "Ends", body: endsView },
    models: { title: "Models", body: modelsView },
    new: { title: "Share your credits", body: newView },
    mate: { title: "Choose a mate", body: mateView },
    provider: { title: "Provider", body: providerView },
    received: { title: "Shared with you", body: receivedView },
  };
  const current = views[view.name] || views.main;

  const canShare = Boolean(draft.recipientId && draft.provider);
  const footer =
    view.name === "invite" ? (
      <Button
        type="button"
        className="h-12 w-full text-base font-semibold"
        disabled={busy || !inviteDraft.provider}
        onClick={createInviteLink}
      >
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Make the link
      </Button>
    ) : view.name === "new" ? (
      <Button type="button" className="h-12 w-full text-base font-semibold" disabled={busy || !canShare} onClick={create}>
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {mate ? `Share with ${firstName(mate.name)}` : "Share credits"}
      </Button>
    ) : view.name === "received" && viewing ? (
      inUse ? (
        <Button type="button" variant="outline" className="h-12 w-full text-base font-semibold" disabled>
          In use
        </Button>
      ) : (
        <Button
          type="button"
          className="h-12 w-full text-base font-semibold"
          onClick={() => {
            onUseShare(viewing);
            onClose();
          }}
        >
          Use these credits
        </Button>
      )
    ) : undefined;

  return (
    <DetailSheet
      open={open}
      onOpenChange={(o) => !o && close()}
      title={current.title}
      onBack={view.name === "main" ? undefined : back}
      footer={footer}
    >
      {current.body}
    </DetailSheet>
  );
};

export default CreditSharesSheet;
